// Эндпоинты входа. Формы — строго по contracts/openapi/bark-core.v1.yaml: контракт правит
// только сессия ядра, здесь его зеркало, а не вольный пересказ.

import { request } from '@/shared/api/client';

export type DeviceKind = 'web' | 'desktop' | 'ios' | 'android';

export interface LoginRequest {
  /** Имя входа. */
  login: string;
  /** Фраза для входа. */
  password: string;
  device_kind?: DeviceKind;
  device_name?: string | null;
}

/** Кто вошёл. Разрешения приходят готовым списком кодов, клиент их из ролей не выводит. */
export interface UserPayload {
  id: string;
  display_name: string;
  /** СВОЁ имя входа. Чужое сервер не отдаёт нигде: по нему подбирают фразу. */
  login: string;
  employee_id?: string | null;
  roles: string[];
  permissions: string[];
  /** Поля, которые этому человеку не показываются нигде. */
  hidden_fields: string[];
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  /** Срок жизни токена доступа, секунд. */
  expires_in: number;
  /** Минимальная поддерживаемая версия мобильного приложения. */
  min_app_version: string;
  /**
   * Контур демонстрационный: данные обезличены, поверх интерфейса рисуется полоса «демо».
   * Свойство КОНТУРА, а не человека, поэтому лежит рядом с минимальной версией.
   */
  demo_banner?: boolean;
  user: UserPayload;
}

// У auth-эндпоинтов retryOn401 выключен: попытка «обновиться и повторить» на самом
// обновлении дала бы цикл из 401.

export function login(body: LoginRequest): Promise<AuthResponse> {
  return request<AuthResponse>('/api/v1/auth/login', {
    method: 'POST',
    body,
    auth: false,
    retryOn401: false,
  });
}

/**
 * Вход в демонстрацию: ни имени, ни фразы.
 *
 * Дверь есть ТОЛЬКО на показательном контуре — на сервере организации этого адреса не
 * существует вовсе, и ответ на него неотличим от опечатки в пути. Поэтому вызывать её
 * можно лишь после того, как адрес переключён на показательный.
 *
 * `kind: 'cabinet'` даёт гостя финансового отдела: у кабинета по ту сторону экрана другой
 * человек, чем у телефона, и гость охранника показал бы здесь пустые разделы.
 */
export function demoSession(deviceName?: string | null): Promise<AuthResponse> {
  return request<AuthResponse>('/api/v1/demo/session', {
    method: 'POST',
    body: { device_kind: 'web', device_name: deviceName ?? null, kind: 'cabinet' },
    auth: false,
    retryOn401: false,
  });
}

export function refresh(refreshToken: string): Promise<AuthResponse> {
  return request<AuthResponse>('/api/v1/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken },
    auth: false,
    retryOn401: false,
  });
}

/** Выход: отзыв токена обновления. Сервер отвечает 204 без тела. */
export function logout(refreshToken: string): Promise<void> {
  return request<void>('/api/v1/auth/logout', {
    method: 'POST',
    body: { refresh_token: refreshToken },
    retryOn401: false,
  });
}

/** Кто вошёл: роли, разрешения, скрытые поля. */
export function me(): Promise<UserPayload> {
  return request<UserPayload>('/api/v1/me');
}
